<template>
  <Component
    ref="modal"
    :is="maxSmall ? Drawer : Dialog"
    :open="open"
    @update:open="handleOpenChange"
    :variant="variant"
    background="white"
    :theme="theme"
    :title="title"
    :description="description"
    :teleport="maxSmall ? true : teleport"
    :withSearch="withSearch"
    :showFooter="showFooter"
    :size="size"
    :cancelLabel="cancelLabel"
    :confirmLabel="confirmLabel"
    :showCancel="showCancel"
    :showConfirm="showConfirm"
    :disableConfirm="disableConfirm"
    :alignItems="alignItems"
    :alignButtons="alignButtons"
    @cancel="$emit('cancel')"
    @confirm="$emit('confirm')"
  >
    <slot />
    <template v-if="withClear || $slots.header" #header>
      <slot name="header">
        <DrawerHeader
          ref="header"
          :title="title"
          :description="description"
          :leadingAction="{
            label: $t('components.buttons.clear'),
            event: 'clear',
          }"
          :withSearch="withSearch"
          @clear="handleClear"
        >
          <template #search="{ query }">
            <slot name="search" :query="query" />
          </template>
        </DrawerHeader>
      </slot>
    </template>
    <template #tabs>
      <slot name="tabs" />
    </template>
    <template #search="{ query }">
      <slot name="search" :query="query" />
    </template>
    <template #content>
      <slot name="content" />
    </template>
    <template v-if="$slots.footer" #footer>
      <slot name="footer" />
    </template>
  </Component>
</template>

<script setup>
import { computed, ref, watchEffect } from "vue";
import { Dialog } from "@/SharedComponents/Dialog";
import { Drawer, DrawerHeader } from "@/SharedComponents/Drawer";
import { useBreakpoints } from "@/Composables/useBreakpoints";

const { maxSmall } = useBreakpoints();

const emit = defineEmits(["update:open", "cancel", "confirm", "clear"]);

const modal = ref(null);
const header = ref(null);

const props = defineProps({
  open: { type: Boolean, default: undefined },
  variant: { type: String, default: "modal" },
  theme: { type: String, required: false },
  size: { type: String, required: false },
  title: { type: String, required: false },
  description: { type: String, required: false },
  teleport: { type: Boolean, default: true },
  withClear: { type: Boolean, required: false },
  withSearch: { type: Boolean, required: false },
  showFooter: { type: Boolean, default: true },
  cancelLabel: { type: String, required: false },
  confirmLabel: { type: String, required: false },
  showCancel: { type: Boolean, default: true },
  showConfirm: { type: Boolean, default: true },
  disableConfirm: { type: Boolean, required: false },
  alignItems: { type: String, required: false },
  alignButtons: { type: String, required: false },
});

const open = ref(props.open);

watchEffect(() => {
  open.value = props.open;
});

const handleOpenChange = (value) => {
  open.value = value;
  emit("update:open", value);
};

const handleClear = () => {
  open.value = false;
  emit("clear");
};

function close() {
  open.value = false;
}

defineExpose({
  query: computed(() => header.value?.query ?? modal.value?.query),
  clearQuery: () =>
    header.value ? header.value.clearQuery() : modal.value.clearQuery(),
  close,
});
</script>
